import React, { useEffect, useRef, useState, useCallback } from "react";

/* ─── Types ─────────────────────────────────────────────── */
interface DeviceInfo {
  device_id: string;
  state: string;
  ip_address: string | null;
  mac_address: string | null;
  model: string | null;
  android_version: string | null;
  is_wireless: boolean;
}

interface BridgeStatus {
  adb_available: boolean;
  device_connected: boolean;
  device: DeviceInfo | null;
}

interface AIAnalysis {
  current_app: string | null;
  screen_type: string;
  messages: Array<{ sender: string; content: string; timestamp?: string }>;
  caller_name: string | null;
  notifications: Array<{ app: string; text: string }>;
  suggested_reply: string | null;
  suggested_actions: Array<{ action: string; label: string }>;
  confidence: number;
}

/* ─── API helpers ───────────────────────────────────────── */
const API = "/api/phone-bridge";

async function apiFetch(path: string, opts?: RequestInit) {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

const postJSON = (path: string, body: any) =>
  apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/* ─── Android key codes ─────────────────────────────────── */
const KEYCODES = [
  { label: "Home", code: 3 },
  { label: "Back", code: 4 },
  { label: "Recents", code: 187 },
  { label: "Vol ▲", code: 24 },
  { label: "Vol ▼", code: 25 },
  { label: "Power", code: 26 },
];

/* ─── Component ─────────────────────────────────────────── */
const SmartPhoneBridge: React.FC = () => {
  const [status, setStatus] = useState<BridgeStatus | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const streamBlobUrl = useRef<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const addLog = (msg: string) =>
    setLog((prev) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev.slice(0, 49)]);

  /* ── Fetch status ─────────────────────────────────────── */
  const fetchStatus = useCallback(async () => {
    try {
      const data: BridgeStatus = await apiFetch("/status");
      setStatus(data);
      setError(null);
    } catch (e: any) {
      setError("Backend unreachable — restart Docker backend.");
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 8000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  /* ── Connect ──────────────────────────────────────────── */
  const handleConnect = async () => {
    setConnecting(true);
    addLog("Connecting to preferred device (MAC)…");
    try {
      const res = await apiFetch("/connect/mac", { method: "POST" });
      addLog(`Connected: ${res.device?.model || res.device?.device_id}`);
      await fetchStatus();
    } catch (e: any) {
      addLog(`Connect failed: ${e.message}`);
      setError(e.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    stopStream();
    await apiFetch("/disconnect", { method: "POST" });
    addLog("Disconnected");
    setScreenshotUrl(null);
    setAnalysis(null);
    await fetchStatus();
  };

  /* ── Single screenshot ────────────────────────────────── */
  const takeScreenshot = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/screenshot`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      if (screenshotUrl) URL.revokeObjectURL(screenshotUrl);
      setScreenshotUrl(URL.createObjectURL(blob));
      addLog("Screenshot captured");
    } catch (e: any) {
      addLog(`Screenshot error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  /* ── Live stream (WebSocket) ──────────────────────────── */
  const startStream = () => {
    if (wsRef.current) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${window.location.host}/api/phone-bridge/ws/stream`);
    ws.binaryType = "blob";
    ws.onmessage = (evt) => {
      if (!(evt.data instanceof Blob)) return;
      const url = URL.createObjectURL(evt.data);
      if (imgRef.current) imgRef.current.src = url;
      if (streamBlobUrl.current) URL.revokeObjectURL(streamBlobUrl.current);
      streamBlobUrl.current = url;
    };
    ws.onopen = () => { setStreaming(true); addLog("Live stream started"); };
    ws.onclose = () => { setStreaming(false); addLog("Live stream ended"); wsRef.current = null; };
    ws.onerror = () => { addLog("WebSocket error"); ws.close(); };
    wsRef.current = ws;
  };

  const stopStream = () => {
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    setStreaming(false);
  };

  /* ── AI analysis ──────────────────────────────────────── */
  const runAnalysis = async () => {
    setAnalysing(true);
    addLog("Running AI analysis…");
    try {
      const res = await apiFetch("/ai/analyze", { method: "POST" });
      setAnalysis(res.analysis);
      addLog(`AI: ${res.analysis.screen_type} (${Math.round(res.analysis.confidence * 100)}% confidence)`);
    } catch (e: any) {
      addLog(`Analysis failed: ${e.message}`);
    } finally {
      setAnalysing(false);
    }
  };

  /* ── Input controls ───────────────────────────────────── */
  const sendKey = (code: number, label: string) => {
    postJSON("/input/key", { keycode: code })
      .then(() => addLog(`Key: ${label}`))
      .catch(() => addLog(`Key failed: ${label}`));
  };

  /* ── Cleanup ──────────────────────────────────────────── */
  useEffect(() => () => { stopStream(); }, []);

  /* ── Styles ───────────────────────────────────────────── */
  const card: React.CSSProperties = {
    background: "rgba(19,27,46,0.75)",
    backdropFilter: "blur(14px)",
    border: "1px solid rgba(200,191,255,0.2)",
    borderRadius: 14,
    padding: "1.25rem",
  };

  const btn = (color = "#ffd6a5"): React.CSSProperties => ({
    background: "transparent",
    border: `1px solid ${color}`,
    color,
    borderRadius: 8,
    padding: "6px 14px",
    cursor: "pointer",
    fontSize: 13,
    fontFamily: "Space Grotesk, sans-serif",
  });

  const connectedColor = "#b8d58d";
  const dangerColor = "#ff6b6b";

  return (
    <div style={{ background: "#0b1326", minHeight: "100vh", padding: "1.5rem", fontFamily: "Space Grotesk, Manrope, sans-serif", color: "#f0f0f0" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: "1.5rem" }}>
        <span style={{ fontSize: 28 }}>📱</span>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: "#ffd6a5" }}>Smart Phone Bridge</h1>
          <p style={{ margin: 0, fontSize: 13, color: "#8899aa" }}>ADB mirror · AI analysis · Remote control</p>
        </div>
      </div>

      {error && (
        <div style={{ background: "rgba(255,107,107,0.15)", border: "1px solid #ff6b6b", borderRadius: 10, padding: "0.75rem 1rem", marginBottom: "1rem", color: "#ff6b6b", fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Status row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: "1.5rem" }}>
        {/* ADB status */}
        <div style={card}>
          <div style={{ fontSize: 12, color: "#8899aa", marginBottom: 4 }}>ADB</div>
          <div style={{ color: status?.adb_available ? connectedColor : dangerColor, fontWeight: 700 }}>
            {status?.adb_available ? "Available" : "Not found"}
          </div>
        </div>
        {/* Device status */}
        <div style={card}>
          <div style={{ fontSize: 12, color: "#8899aa", marginBottom: 4 }}>Device</div>
          <div style={{ color: status?.device_connected ? connectedColor : "#8899aa", fontWeight: 700 }}>
            {status?.device?.model || (status?.device_connected ? status?.device?.device_id : "Not connected")}
          </div>
          {status?.device?.android_version && (
            <div style={{ fontSize: 11, color: "#8899aa" }}>Android {status.device.android_version}</div>
          )}
        </div>
        {/* Stream status */}
        <div style={card}>
          <div style={{ fontSize: 12, color: "#8899aa", marginBottom: 4 }}>Stream</div>
          <div style={{ color: streaming ? connectedColor : "#8899aa", fontWeight: 700 }}>
            {streaming ? "Live" : "Idle"}
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "1rem" }}>
        {/* Left: screen */}
        <div>
          {/* Action bar */}
          <div style={{ display: "flex", gap: 8, marginBottom: "0.75rem", flexWrap: "wrap" }}>
            {!status?.device_connected ? (
              <button style={btn()} onClick={handleConnect} disabled={connecting}>
                {connecting ? "Connecting…" : "Connect Device"}
              </button>
            ) : (
              <button style={btn(dangerColor)} onClick={handleDisconnect}>Disconnect</button>
            )}
            <button style={btn()} onClick={takeScreenshot} disabled={!status?.device_connected || loading}>
              {loading ? "Capturing…" : "Screenshot"}
            </button>
            {!streaming ? (
              <button style={btn(connectedColor)} onClick={startStream} disabled={!status?.device_connected}>
                ▶ Live Stream
              </button>
            ) : (
              <button style={btn(dangerColor)} onClick={stopStream}>⏹ Stop Stream</button>
            )}
            <button style={btn("rgba(200,191,255,0.8)")} onClick={runAnalysis} disabled={!status?.device_connected || analysing}>
              {analysing ? "Analysing…" : "🧠 AI Analyse"}
            </button>
          </div>

          {/* Phone screen */}
          <div style={{ ...card, aspectRatio: "9/16", maxWidth: 360, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", position: "relative" }}>
            {streaming ? (
              <img
                ref={imgRef}
                alt="Live stream"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }}
              />
            ) : screenshotUrl ? (
              <img
                src={screenshotUrl}
                alt="Screenshot"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", borderRadius: 8 }}
              />
            ) : (
              <div style={{ textAlign: "center", color: "#8899aa" }}>
                <div style={{ fontSize: 48, marginBottom: 8 }}>📱</div>
                <div style={{ fontSize: 13 }}>Connect a device and take a screenshot</div>
              </div>
            )}
          </div>

          {/* Key controls */}
          <div style={{ marginTop: "0.75rem", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {KEYCODES.map(({ label, code }) => (
              <button
                key={code}
                style={btn()}
                onClick={() => sendKey(code, label)}
                disabled={!status?.device_connected}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Right: AI panel + log */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {/* AI Analysis */}
          <div style={card}>
            <div style={{ fontSize: 13, color: "#ffd6a5", fontWeight: 700, marginBottom: 8 }}>🧠 AI Analysis</div>
            {analysis ? (
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                <div><span style={{ color: "#8899aa" }}>App:</span> {analysis.current_app || "—"}</div>
                <div><span style={{ color: "#8899aa" }}>Screen:</span> {analysis.screen_type}</div>
                <div><span style={{ color: "#8899aa" }}>Confidence:</span> {Math.round(analysis.confidence * 100)}%</div>
                {analysis.caller_name && (
                  <div style={{ marginTop: 6, color: "#b8d58d" }}>📞 Caller: {analysis.caller_name}</div>
                )}
                {analysis.messages.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: "#8899aa", fontSize: 11, marginBottom: 4 }}>MESSAGES</div>
                    {analysis.messages.map((m, i) => (
                      <div key={i} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 6, padding: "4px 8px", marginBottom: 4 }}>
                        <strong>{m.sender}:</strong> {m.content}
                      </div>
                    ))}
                  </div>
                )}
                {analysis.suggested_reply && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: "#8899aa", fontSize: 11, marginBottom: 4 }}>SUGGESTED REPLY</div>
                    <div style={{ background: "rgba(200,191,255,0.08)", border: "1px solid rgba(200,191,255,0.2)", borderRadius: 6, padding: "6px 8px", fontSize: 12 }}>
                      {analysis.suggested_reply}
                    </div>
                  </div>
                )}
                {analysis.suggested_actions.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: "#8899aa", fontSize: 11, marginBottom: 4 }}>ACTIONS</div>
                    {analysis.suggested_actions.map((a, i) => (
                      <div key={i} style={{ fontSize: 11, color: "#ffd6a5" }}>▸ {a.label}</div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#8899aa" }}>Run AI Analyse to see screen intelligence.</div>
            )}
          </div>

          {/* Activity log */}
          <div style={{ ...card, flex: 1 }}>
            <div style={{ fontSize: 13, color: "#ffd6a5", fontWeight: 700, marginBottom: 8 }}>Activity Log</div>
            <div style={{ fontSize: 11, color: "#8899aa", maxHeight: 260, overflowY: "auto" }}>
              {log.length === 0 ? (
                <div>No activity yet.</div>
              ) : (
                log.map((l, i) => <div key={i} style={{ marginBottom: 2 }}>{l}</div>)
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartPhoneBridge;
